import React, { useState } from 'react';
import { Container, Typography, CircularProgress, Box, Alert } from '@mui/material';
import { useDashboard } from '../hooks/useDashboard';
import KPIStats from '../components/dashboard/KPIStats';
import SalesTrendChart from '../components/dashboard/SalesTrendChart';
import OrderStatusChart from '../components/dashboard/OrderStatusChart';
import TopProductsTable from '../components/dashboard/TopProductsTable';
import LowStockTable from '../components/dashboard/LowStockTable';
import SalesFilter from '../components/salesAnalytics/SalesFilter';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../context/WarehouseContext';

const Dashboard = () => {
  const { t } = useTranslation();
  const { getDashboardData } = useDashboard();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const { selectedWarehouseIds } = useWarehouseContext();

  const [period, setPeriod] = useState('last_30_days');

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      from_date: startOfMonth.toISOString().split('T')[0],
      to_date: endOfMonth.toISOString().split('T')[0],
    };
  });

  const fetchingRef = React.useRef(false);

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  const fetchData = React.useCallback(async (force = false) => {
    // For custom period, require dates
    if (period === 'custom' && (!dateRange.from_date || !dateRange.to_date)) return;

    // Prevent double fetch
    if (fetchingRef.current) return;

    const warehouseKey = selectedWarehouseIds?.length > 0 ? `_w${selectedWarehouseIds.join('_')}` : '';
    const cacheKey = period === 'custom'
      ? `dashboard_data_custom_${dateRange.from_date}_${dateRange.to_date}${warehouseKey}`
      : `dashboard_data_${period}${warehouseKey}`;
    const cachedData = localStorage.getItem(cacheKey);

    if (!force && cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        if (parsed) {
          setData(parsed);
          return;
        }
      } catch (e) {
        console.error("Failed to parse cached dashboard data", e);
        localStorage.removeItem(cacheKey);
      }
    }

    fetchingRef.current = true;
    setLoading(true);
    setFetchError(null);

    const params = { period };

    // Only include dates for custom period
    if (period === 'custom') {
      params.from_date = dateRange.from_date;
      params.to_date = dateRange.to_date;
    }

    const result = await getDashboardData(params);

    if (result.success) {
      setData(result.data);
      localStorage.setItem(cacheKey, JSON.stringify(result.data));
    } else {
      setFetchError(result.error);
    }

    setLoading(false);
    fetchingRef.current = false;
  }, [period, dateRange, getDashboardData, selectedWarehouseIds]);

  // Initial fetch on mount
  React.useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouseIds]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={5} display="flex" justifyContent="space-between" alignItems="flex-end">
        <div>
          <Typography
            variant="h3"
            component="h1"
            fontWeight="800"
            sx={{
              background: 'linear-gradient(45deg, #05445E 30%, #189AB4 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            {t('dashboard.title')}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 500 }}>
            {t('dashboard.subtitle')}
          </Typography>
        </div>
        {data && (
          <Box textAlign="right">
            <Typography variant="caption" display="block" color="textSecondary" sx={{ fontWeight: 500, mb: 0.5 }}>
              {t('dashboard.generatedAt')}
            </Typography>
            <Typography variant="body2" fontWeight="600" color="primary">
              {new Date(data.generated_at).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Date Filter */}
      <SalesFilter
        fromDate={dateRange.from_date}
        toDate={dateRange.to_date}
        onFromDateChange={(val) => handleDateChange('from_date', val)}
        onToDateChange={(val) => handleDateChange('to_date', val)}
        onSubmit={() => fetchData(true)}
        loading={loading}
        period={period}
        onPeriodChange={handlePeriodChange}
      />

      {fetchError && (
        <Alert severity="error" sx={{ mb: 3 }}>{fetchError || 'Failed to load dashboard data'}</Alert>
      )}

      {loading && !data && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      )}

      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {/* KPI Stats */}
          <KPIStats kpis={data.kpis} />

          {/* Charts Row */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px', minWidth: 320 }}>
              <SalesTrendChart data={data.sales_trend} />
            </div>
            <div style={{ flex: '1 1 320px', minWidth: 280 }}>
              <OrderStatusChart data={data.order_status} />
            </div>
          </div>

          {/* Tables Row */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px', minWidth: 320 }}>
              <TopProductsTable products={data.top_products} />
            </div>
            <div style={{ flex: '1 1 420px', minWidth: 320 }}>
              <LowStockTable products={data.low_stock_products} />
            </div>
          </div>
        </div>
      )}
    </Container>
  );

};

export default Dashboard;